import { useEffect, useRef } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback, useButtonPressedCallback, ModalDisplayedTracker } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';

import { WistiaVideo } from '#client/components/WistiaVideo';
import useLocalStorage from '#client/hooks/useLocalStorage';
import { removeUrlParam } from '#client/pages/Home/utils';
import { useIsFormationCompleted } from '#client/pages/MarketingTools/hooks/useIsFormationCompleted';
import { MARKETING_TOOLS_MODAL_PARAM_KEY } from '#client/pages/MarketingTools/utils/constants';
import { GET_WEBSITES_DATA } from '#client/pages/MarketingTools/utils/queries';
import { mapBusinessEntityQuery } from '#client/utils/businessEntity';
import { generateEntitlements } from '#client/utils/entitlements';
import {
  dataTrackingPropsType
} from '#client/utils/reporting';

import { LD_ENABLE_E_COMMERCE_EDITOR_SELECTOR, LD_RELEASE_TRY_BEFORE_YOU_BUY } from '../../constants';
import EditorOption from '../EditorOption/EditorOption';
import StepOnline from '../StepOnline/StepOnline';
import { stepsToGetOnline } from './stepsToGetOnline';

const EditorsDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '100vw',
    height: '100vh',
    maxHeight: 'unset',
    margin: '0 auto',
    padding: 0,
    borderRadius: 0
  }
});

const StepsCardsContainer = styled(Stack)(({ theme }) => ({
  padding: `0px ${theme.spacing(12.5)}`
}));

const StyledEditorsDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.focus50.main,
  height: '100%'
}));

export const StyledSharedAssetElementMobile = styled(SharedAsset)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'none'
  }
}));

export const StyledSharedAssetElementDesktop = styled(SharedAsset)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'unset'
  }
}));

const videoUrl = 'https://zenbusiness.wistia.com/medias/rouwf03sio';

const EditorsModal = ({
  isOpen, hasActiveWebsite, setIsOpen, dataTrackingProps
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessEntityUuid } = useBusinessEntity();
  // NOTE: Local storage key naming convention: <app URL pattern>.<business entity uuid>.<key name>
  // This naming convention is flexible enough to have keys per business entity per application
  const [showECommerceComingSoon, setShowECommerceComingSoon] = useLocalStorage(`d.${businessEntityUuid}.show-e-commerce-coming-soon`, false);
  const scrollableElementRef = useRef();

  const { loading, data: queryData = {} } = useQuery(GET_WEBSITES_DATA, {
    variables: {
      businessEntityUuid
    }
  });

  const data = mapBusinessEntityQuery(queryData);
  const { orderTasksLegacy = [] } = loading ? {} : data.orderTasksLegacy;
  const domainConnected = loading
    ? false
    : data.domains.domains.some(
      (domain) => !domain.deleted && domain.websiteConnectionStatus === 'connected'
    );

  const isFormationCompleted = useIsFormationCompleted(orderTasksLegacy);

  const releaseTryBeforeYouBuy = useExperimentFlag(LD_RELEASE_TRY_BEFORE_YOU_BUY, false);
  const enableECommerceEditor = useExperimentFlag(LD_ENABLE_E_COMMERCE_EDITOR_SELECTOR, false);

  // TODO: Migrate to the new service to fetch entitlements, https://counsl.atlassian.net/browse/WEB-1807
  const entitlements = generateEntitlements(orderTasksLegacy);
  const hasPurchasedWebsite = entitlements.has('static_website');
  const websiteLabels = releaseTryBeforeYouBuy && !hasPurchasedWebsite ? '&websiteLabels=tryBeforeYouBuy' : '';

  const handleClose = () => {
    removeUrlParam({ navigate, location, paramKey: MARKETING_TOOLS_MODAL_PARAM_KEY });
    setIsOpen(false);
  };

  const trackVideoPlay = useButtonPressedCallback({
    elementName: 'play_video',
    ...dataTrackingProps
  });

  const trackSimpleClick = useLinkPressedCallback({
    elementName: 'choose_simple',
    ...dataTrackingProps
  });

  const trackAdvancedClick = useLinkPressedCallback({
    elementName: 'choose_advanced',
    ...dataTrackingProps
  });

  const trackECommerceClick = useLinkPressedCallback({
    elementName: 'advanced_eCommerce',
    ...dataTrackingProps
  });

  const enableZenwebsitesMigration = useExperimentFlag('enable-zenwebsites-migration', false);
  const editorsHrefPrefix = enableZenwebsitesMigration ? '/websites' : '/w';
  const editors = {
    simple: {
      title: 'I want a quick and simple website',
      imageUrl: '/s2/raster/feature-simple-editor.png',
      buttonText: 'Simple editor',
      ButtonProps: {
        color: 'primary',
        variant: 'contained',
        sx: (theme) => ({
          marginLeft: theme.spacing(0)
        }),
        href: `${editorsHrefPrefix}/loading?businessEntityUuid=${businessEntityUuid}&websiteType=dudaSimple${websiteLabels}`,
        target: '_blank',
        onClick: (e) => {
          trackSimpleClick(e);
          handleClose(); /* Close full-page modal to show parent page */
        }
      }
    },
    advanced: {
      title: 'I need a more flexible website',
      imageUrl: '/picture/pbf-feature-online-tools-custom-website.png',
      buttonText: 'Advanced editor',
      ButtonProps: {
        color: 'primary',
        variant: 'contained',
        href: `${editorsHrefPrefix}/templates?businessEntityUuid=${businessEntityUuid}&websiteType=dudaNative${websiteLabels}`,
        target: '_self',
        onClick: trackAdvancedClick
      }
    },
    eCommerce: {
      title: 'I need a store on my website',
      imageUrl: '/s2/raster/ecommerce-editor.png',
      buttonText: 'Sell online',
      ButtonProps: {
        color: 'primary',
        variant: 'contained',
        href: '#', /* so the Button component renders a anchor element, thus use the link_pressed event for tracking */
        disabled: showECommerceComingSoon,
        onClick: (e) => {
          trackECommerceClick(e);

          // If user has scrolled down, scroll modal to the top, then show the "coming soon" state
          if (scrollableElementRef.current.scrollTop > 0) {
            scrollableElementRef.current.scroll({
              top: 0, behavior: 'smooth'
            });
            setTimeout(() => {
              setShowECommerceComingSoon(true);
            }, 800 /* 800ms best waiting value to view the state change after scrolled to top */);
          } else {
            setShowECommerceComingSoon(true);
          }
        }
      }
    }
  };

  const isAllowedToChooseEditor = (releaseTryBeforeYouBuy || hasPurchasedWebsite)
    && !hasActiveWebsite;
  const open = isOpen && isAllowedToChooseEditor;

  useEffect(() => {
    // Guard implemented to avoid a user-entity without formation "completed" to create a website,
    // therefore redirect the user to formation flow page.
    if (!open) return; /* Only redirect when modal is opened */
    if (isFormationCompleted) return; /* Do not redirect */

    window.location.href = '/r/incomplete';
  }, [open, isFormationCompleted]);

  const stepBuilderPick = stepsToGetOnline['builder-pick'];
  const stepWebsitePurchase = stepsToGetOnline[releaseTryBeforeYouBuy ? 'website-purchase-completed' : 'website-purchase'];
  const stepConnectDomain = stepsToGetOnline['connect-domain'];
  stepConnectDomain.completed = domainConnected;

  // To avoid flicker behavior render until formation request has ended and formation completed
  if (!isFormationCompleted || orderTasksLegacy.length < 1) return null;

  const renderECommerceButton = (() => {
    if (!enableECommerceEditor) return null;

    if (showECommerceComingSoon) {
      return (
        <>
          <StyledEditorsDivider
            sx={{ display: { xs: 'none', lg: 'block' } }}
            orientation="vertical"
            flexItem
          />
          <Divider
            sx={{ width: '100%', my: 7, display: { xs: 'block', lg: 'none' } }}
            orientation="horizontal"
          />
          <Grid item>
            <EditorOption
              title="eCommerce coming soon!"
              buttonText="Sell online"
              ButtonProps={{
                color: 'primary',
                variant: 'contained',
                disabled: true
              }}
            >
              <Box minHeight={200} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
                <SharedAsset src="/s2/vector/calendar-and-dollar-sign.svg" width={64} />
                <Text size="md">
                  <Text tag="span" bold>You&apos;ll be able to set up an online store soon.</Text>
                  {' '}
                  We&apos;ll let you know when it&apos;s ready!
                </Text>
              </Box>
            </EditorOption>
          </Grid>
        </>
      );
    }

    return (
      <>
        <StyledEditorsDivider
          sx={{ display: { xs: 'none', lg: 'block' } }}
          orientation="vertical"
          flexItem
        />
        <Divider
          sx={{ width: '100%', my: 7, display: { xs: 'block', lg: 'none' } }}
          orientation="horizontal"
        />
        <Grid item>
          <EditorOption {...editors.eCommerce} />
        </Grid>
      </>
    );
  })();

  return (
    <EditorsDialog
      maxWidth="unset"
      open={open}
      onClose={handleClose}
      PaperProps={{
        ref: scrollableElementRef
      }}
    >
      <ModalDisplayedTracker
        modalName="duda_select_editor_modal"
        open={open}
        {...dataTrackingProps}
        modalFunction="info"
      />
      <Grid
        container
        sx={(theme) => ({
          alignItems: 'center',
          height: '72px',
          py: 3,
          px: 2,
          [theme.breakpoints.up('md')]: {
            height: '96px',
            p: 3
          }
        })}
      >
        <Grid item xs />
        <Grid item>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="large"
            sx={(theme) => ({
              padding: '5px',
              [theme.breakpoints.up('md')]: {
                padding: '6px'
              }
            })}
          >
            <StyledSharedAssetElementMobile
              src="/icon/micro/close-x.svg"
              width={14}
            />
            <StyledSharedAssetElementDesktop
              src="/icon/micro/close-x.svg"
              width={19}
            />
          </IconButton>
        </Grid>
      </Grid>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
        sx={(theme) => ({
          padding: '0px 16px 24px',
          margin: 'auto',
          [theme.breakpoints.up('lg')]: {
            mt: 9,
            maxWidth: '1024px'
          }
        })}
      >
        <Grid item>
          <Text
            size="md"
            sx={{
              fontSize: { sm: '32px' },
              lineHeight: { sm: '40px' }
            }}
          >
            Choose your website editor
          </Text>
        </Grid>
        <Grid
          container
          direction="row"
          alignItems="stretch"
          sx={(theme) => ({
            py: 7,
            px: 0,
            justifyContent: 'space-evenly',
            alignItems: 'center',
            [theme.breakpoints.up('lg')]: {
              justifyContent: 'space-between',
              py: 8
            }
          })}
        >
          <Grid item>
            <EditorOption {...editors.simple} />
          </Grid>
          <StyledEditorsDivider
            sx={{ display: { xs: 'none', lg: 'block' } }}
            orientation="vertical"
            flexItem
          />
          <Divider
            sx={{ width: '100%', my: 7, display: { xs: 'block', lg: 'none' } }}
            orientation="horizontal"
          />
          <Grid item>
            <EditorOption {...editors.advanced} />
          </Grid>
          {renderECommerceButton}
        </Grid>
        <Grid item width="100%">
          <Grid item paddingTop={5} paddingBottom={5}>
            <Title sx={{
              height: '100%',
              textAlign: 'center',
              padding: 3
            }}
            >
              See the tool in action
            </Title>
            <WistiaVideo
              id={videoUrl?.split('/').pop()}
              onPlay={trackVideoPlay}
            />
          </Grid>
          <Paper
            elevation={5}
            sx={(theme) => ({
              background: theme.palette.focus10.main,
              borderRadius: '16px',
              marginTop: theme.spacing(8),
              marginBottom: theme.spacing(8),
              padding: theme.spacing(3),
              paddingLeft: theme.spacing(4),
              width: '100%',
              '& Grid': {
                alignSelf: 'center'
              }
            })}
          >
            <Grid container direction="row" wrap="nowrap">
              <Grid item paddingRight="12px" alignSelf="center">
                <SharedAsset src="/s2/vector/speed-security.svg" width={64} alt="" />
              </Grid>
              <Grid item alignSelf="center">
                <Text
                  color="focus90"
                  sx={(theme) => ({
                    fontWeight: 600,
                    fontSize: '23px',
                    lineHeight: '20px',
                    marginBottom: 1,
                    [theme.breakpoints.up('md')]: {
                      fontSize: '23px',
                      lineHeight: '27px',
                      marginBottom: 0.5
                    }
                  })}
                >
                  All editors have the same security, speed, and mobile-friendly design.
                </Text>
                <Text
                  color="focus90"
                  sx={(theme) => ({
                    fontWeight: 400,
                    fontSize: '15px',
                    lineHeight: '16px',
                    [theme.breakpoints.up('md')]: {
                      fontSize: '16px',
                      lineHeight: '24px'
                    }
                  })}
                >
                  You can always move to the advanced editor if you want more features.
                </Text>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid
          item
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            [theme.breakpoints.down('lg')]: {
              padding: 0
            }
          })}
        >
          <Grid item>
            <Title align="center" variant="md-alt">
              3 easy steps to get online
            </Title>
          </Grid>
          <Grid
            item
            sx={(theme) => ({
              paddingTop: theme.spacing(6),
              paddingBottom: theme.spacing(6),
              paddingLeft: theme.spacing(27.5),
              paddingRight: theme.spacing(27.5),
              '& p': {
                fontSize: '1.50rem'
              }
            })}
          >
            <Text size="lg">
              No matter where you are in your business journey, you’ll have access to the right
              tools and templates for your small business.
            </Text>
          </Grid>
          <StepsCardsContainer
            direction="row"
            divider={(
              <Divider
                orientation="horizontal"
                flexItem
                sx={(theme) => ({
                  '&.MuiDivider-flexItem': {
                    margin: `${theme.spacing(7)} auto`
                  },
                  backgroundColor: 'transparent',
                  borderColor: theme.palette.focus50.main,
                  height: 'auto',
                  width: '40px'
                })}
              />
            )}
            spacing={2}
          >
            <StepOnline
              description={stepBuilderPick.description}
              imageURL={stepBuilderPick.imageURL}
              stepCompleted={stepBuilderPick.completed}
            />
            <StepOnline
              description={stepWebsitePurchase.description}
              imageURL={stepWebsitePurchase.imageURL}
              stepCompleted={stepWebsitePurchase.completed}
            />
            <StepOnline
              description={stepConnectDomain.description}
              imageURL={stepConnectDomain.imageURL}
              stepCompleted={stepConnectDomain.completed}
            />
          </StepsCardsContainer>
        </Grid>
        <Grid
          container
          direction="row"
          width="auto"
          gap={8}
          sx={{
            py: 8,
            px: 0,
            justifyContent: 'space-evenly'
          }}
        >
          <Button sx={{ minWidth: 160 }} {...editors.simple.ButtonProps}>Simple</Button>
          <Button sx={{ minWidth: 160 }} {...editors.advanced.ButtonProps}>Advanced</Button>
          { enableECommerceEditor
            && (
              <Button
                sx={{ minWidth: 160 }}
                {...editors.eCommerce.ButtonProps}
              >
                Sell online
              </Button>
            )}
        </Grid>
      </Grid>
    </EditorsDialog>
  );
};

EditorsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  hasActiveWebsite: PropTypes.bool,
  setIsOpen: PropTypes.func.isRequired,
  dataTrackingProps: dataTrackingPropsType
};

export default EditorsModal;
